import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchKeywordRankHistory, getGame } from '../services/games';
import { rankCell } from '../components/RankCell';
import {
  GAMEWITH_HOSTS,
  LANGUAGE_LABELS,
  RANK_HISTORY_RETENTION_DAYS,
  type GamewithHost,
  type RankHistoryPoint,
  type SupportedLanguage,
} from '../types';

const HOST_COLORS: Record<GamewithHost, string> = {
  'gamewith.ai': '#2563eb',
  'gamewith.jp': '#dc2626',
};

/** Competitor lines cycle through these (dashed, to set them apart from
 *  gamewith's solid lines). */
const COMPETITOR_COLORS = ['#ea580c', '#16a34a', '#7c3aed', '#0891b2', '#db2777', '#65a30d', '#a16207', '#475569'];

/** One chart line / table column: a gamewith host or a competitor domain. */
interface Series {
  key: string;
  label: string;
  color: string;
  dashed: boolean;
  /** undefined = not tracked that day (competitor added later). */
  rankAt: (p: RankHistoryPoint) => number | null | undefined;
}

function buildSeries(competitorDomains: string[]): Series[] {
  return [
    ...GAMEWITH_HOSTS.map((host) => ({
      key: host,
      label: host,
      color: HOST_COLORS[host],
      dashed: false,
      rankAt: (p: RankHistoryPoint) => p.rankings[host],
    })),
    ...competitorDomains.map((d, i) => ({
      key: `c:${d}`,
      label: d,
      color: COMPETITOR_COLORS[i % COMPETITOR_COLORS.length],
      dashed: true,
      rankAt: (p: RankHistoryPoint) => p.competitorRankings?.[d],
    })),
  ];
}

/** Search depth (SEARCH_DEPTH in functions/src/services/pipeline.ts) —
 *  rankings can never exceed this, so the chart's Y axis is fixed to it. */
const MAX_RANK = 10;

/**
 * Line chart of rank over time. Y is inverted (rank 1 on top) and 圏外 days
 * appear as gaps in the line. Hand-rolled SVG — the dataset is small (≤180
 * points × 3 hosts) and a chart lib isn't worth the dependency.
 */
function RankChart({ points, series }: { points: RankHistoryPoint[]; series: Series[] }) {
  const W = 800;
  const H = 260;
  const PAD_L = 36;
  const PAD_R = 12;
  const PAD_T = 12;
  const PAD_B = 28;

  const x = (i: number) =>
    points.length === 1
      ? PAD_L + (W - PAD_L - PAD_R) / 2
      : PAD_L + (i * (W - PAD_L - PAD_R)) / (points.length - 1);
  const y = (rank: number) => PAD_T + ((rank - 1) * (H - PAD_T - PAD_B)) / (MAX_RANK - 1);

  // Break the polyline at 圏外 gaps: consecutive ranked points connect, a
  // null in between starts a new segment.
  const pathFor = (s: Series): string => {
    let d = '';
    let penDown = false;
    points.forEach((p, i) => {
      const r = s.rankAt(p);
      if (r === null || r === undefined) {
        penDown = false;
        return;
      }
      d += `${penDown ? 'L' : 'M'}${x(i).toFixed(1)},${y(r).toFixed(1)}`;
      penDown = true;
    });
    return d;
  };

  const yTicks = [1, 3, 5, 7, 10];
  const xLabelIdx = Array.from(
    new Set([0, Math.floor((points.length - 1) / 2), points.length - 1])
  );

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }} role="img" aria-label="順位推移チャート">
      {yTicks.map((t) => (
        <g key={t}>
          <line x1={PAD_L} y1={y(t)} x2={W - PAD_R} y2={y(t)} stroke="#e5e7eb" strokeWidth={1} />
          <text x={PAD_L - 6} y={y(t) + 4} textAnchor="end" fontSize={11} fill="#9ca3af">{t}</text>
        </g>
      ))}
      {xLabelIdx.map((i) => (
        <text key={i} x={x(i)} y={H - 8} textAnchor="middle" fontSize={11} fill="#9ca3af">
          {points[i].date.slice(5)}
        </text>
      ))}
      {series.map((s) => (
        <path
          key={s.key}
          d={pathFor(s)}
          fill="none"
          stroke={s.color}
          strokeWidth={2}
          strokeDasharray={s.dashed ? '6 4' : undefined}
        />
      ))}
      {series.map((s) =>
        points.map((p, i) => {
          const r = s.rankAt(p);
          if (r === null || r === undefined) return null;
          return (
            <circle key={`${s.key}-${p.date}`} cx={x(i)} cy={y(r)} r={2.5} fill={s.color}>
              <title>{`${p.date} ${s.label}: ${r}位`}</title>
            </circle>
          );
        })
      )}
    </svg>
  );
}

export function KeywordHistoryPage() {
  // react-router decodes params, so `keyword` is already the raw query string.
  const { gameId, language, keyword } = useParams<{ gameId: string; language: string; keyword: string }>();
  const lang = language as SupportedLanguage | undefined;
  // undefined = loading, null = no history recorded yet. Fetch failures are
  // kept separate — showing them as "履歴はまだありません" would send
  // operators chasing a pipeline bug when the fetch itself failed.
  const [points, setPoints] = useState<RankHistoryPoint[] | null | undefined>(undefined);
  const [loadError, setLoadError] = useState(false);
  // Columns follow the game's CURRENT competitor list; a domain removed from
  // the settings drops out even though old points still carry it.
  const [competitorDomains, setCompetitorDomains] = useState<string[]>([]);

  useEffect(() => {
    if (!gameId) return;
    let active = true;
    getGame(gameId)
      .then((g) => {
        if (active) setCompetitorDomains(g?.competitorDomains ?? []);
      })
      .catch(() => {
        /* chart still works with gamewith hosts only */
      });
    return () => {
      active = false;
    };
  }, [gameId]);

  useEffect(() => {
    if (!gameId || !lang || !keyword) return;
    let active = true;
    setPoints(undefined);
    setLoadError(false);
    fetchKeywordRankHistory(gameId, lang, keyword)
      .then((p) => {
        if (active) setPoints(p);
      })
      .catch(() => {
        if (active) {
          setLoadError(true);
          setPoints(null);
        }
      });
    return () => {
      active = false;
    };
  }, [gameId, lang, keyword]);

  if (!gameId || !lang || !keyword) return null;

  const pts = points ?? [];
  const series = buildSeries(competitorDomains);

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>順位履歴: {keyword}</h2>
        <Link to={`/games/${gameId}`} className="btn-secondary" style={{ padding: '7px 13px' }}>
          ← ゲーム詳細に戻る
        </Link>
      </div>

      <div className="card">
        <div className="row" style={{ gap: 24 }}>
          <div><span className="muted">言語: </span>{LANGUAGE_LABELS[lang] ?? lang}</div>
          {pts.length > 0 && (
            <div>
              <span className="muted">期間: </span>
              {pts[0].date} 〜 {pts[pts.length - 1].date}
              （{pts.length}日分・最大{RANK_HISTORY_RETENTION_DAYS}日）
            </div>
          )}
          <div className="row" style={{ gap: 12 }}>
            {series.map((s) => (
              <span key={s.key} style={{ fontSize: 12 }}>
                <span style={{ color: s.color }}>{s.dashed ? '┅' : '●'}</span> {s.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {points === undefined ? (
        <div className="card">読み込み中…</div>
      ) : loadError ? (
        <div className="empty card">
          履歴の読み込みに失敗しました。ページを再読み込みしてください。
        </div>
      ) : pts.length === 0 ? (
        <div className="empty card">
          このキーワードの履歴はまだありません。次回の実行から蓄積されます。
        </div>
      ) : (
        <>
          <div className="card">
            <RankChart points={pts} series={series} />
          </div>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>日付</th>
                  {series.map((s) => (
                    <th key={s.key} className={s.dashed ? 'competitor-col' : undefined}>{s.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...pts].reverse().map((p) => (
                  <tr key={p.date}>
                    <td>{p.date}</td>
                    {series.map((s) => {
                      const r = s.rankAt(p);
                      return (
                        <td key={s.key} className={s.dashed ? 'competitor-col' : undefined}>
                          {r === undefined ? <span className="muted" title="この日は未計測">—</span> : rankCell(r)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
